"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AllegroTransformModuleGenerator = void 0;
exports.decodeAllegroTransform = decodeAllegroTransform;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformObject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformObject");
const TransformProject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProject");
const TransformProjectModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProjectModule");
const TransformModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformModule");
const TransformModulePinConnection_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformModulePinConnection");
const TransformSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematic");
const allegroLayers_1 = require("../../../allegroLayers");
const decode_prop_block_1 = require("../../../parsers/ascii/decode/decoders/decode-prop-block");
const AllegroResolved_1 = require("../AllegroResolved");
const AllegroRecordHelpers_1 = require("../AllegroRecordHelpers");
const AllegroResolveDesign_1 = require("../AllegroResolveDesign");
const { SchLayerType } = sigma_engine_shared_1.DesignModels;
const NAME_LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.NameLabel].id;
const VALUE_LABEL_LAYER = allegroLayers_1.AllegroSchLayerMap[SchLayerType.ValueLabel].id;
class AllegroTransformModuleGenerator {
    constructor() {
        this.runsFor = [
            TransformSchematic_1.TransformSchematic,
        ];
    }
    *generate(schematic, rootContext) {
        const project = TransformObject_1.TransformObject.FindParent(schematic, TransformProject_1.TransformProject);
        if (project === null) {
            return;
        }
        const pageNum = schematic.instance.hierarchyEntry.loaded.pageNum;
        const design = rootContext.resolved.designs.find((d) => d.pageNum === pageNum);
        if (design === undefined) {
            return;
        }
        const instPropTextStyle = rootContext.resolved.instPropTextStyle;
        for (const instance of design.instances) {
            yield* emit(schematic, project, rootContext, instance, instPropTextStyle, pageNum);
        }
    }
}
exports.AllegroTransformModuleGenerator = AllegroTransformModuleGenerator;
function* emit(schematic, project, rootContext, resolved, textStyle, pageNum) {
    const component = rootContext.componentsByCellName.get(resolved.cellName);
    if (component === undefined || resolved.rawInstance.kind !== 'component') {
        return;
    }
    const record = resolved.rawInstance.record;
    const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
        name: resolved.refdes,
        component,
        value: resolved.value,
        metadata: Object.keys(resolved.metadata).length > 0 ? resolved.metadata : undefined,
        isDnp: resolved.isDnp,
    });
    yield projectModule;
    const bodyColorOverride = resolved.bodyStroke?.color ?? undefined;
    const moduleProps = {
        ref: resolved.moduleRef,
        footprintRef: (0, AllegroResolved_1.allegroSymbolRefForCell)(resolved.cellName, resolved.view, resolved.sectionNumber),
        center: resolved.center,
        transform: resolved.transform,
        name: resolved.refdes,
        drawProperties: buildModuleDrawProperties(resolved, record, textStyle, rootContext, pageNum),
        ...(bodyColorOverride !== undefined ? { bodyColorOverride } : {}),
    };
    yield new TransformModule_1.TransformModule(schematic, moduleProps, component, projectModule);
    for (const pin of resolved.pins) {
        yield new TransformModulePinConnection_1.TransformModulePinConnection(schematic, {
            position: pin.absolutePosition,
            connection: {
                moduleRef: resolved.moduleRef,
                moduleName: resolved.refdes,
                pinRef: pin.ref,
                skipPositionalCombobulation: true,
                appliesNetName: pin.netName !== null ?
                    { label: pin.netName, labelType: 'system', strategyMetadata: { xconNetName: pin.netName } }
                    : null,
            },
        });
    }
}
const TALL_SYMBOL_ASPECT_THRESHOLD = 3;
function decodeAllegroTransform(matrix, rotationDeg = null, cssBbox = null) {
    let m00 = 1;
    let m01 = 0;
    let m10 = 0;
    let m11 = 1;
    if (matrix !== null && matrix.length >= 9) {
        m00 = matrix[0];
        m01 = matrix[1];
        m10 = matrix[3];
        m11 = matrix[4];
    }
    const det = m00 * m11 - m01 * m10;
    const mirroredX = det < 0;
    const a00 = mirroredX ? -m00 : m00;
    const a10 = mirroredX ? -m10 : m10;
    const matrixAngle = (Math.atan2(a10, a00) * 180) / Math.PI;
    const isExtremelyTall = cssBbox !== null && cssBbox.w > 0 && cssBbox.h / cssBbox.w >= TALL_SYMBOL_ASPECT_THRESHOLD;
    const rawRotation = rotationDeg ?? 0;
    const effectiveRotation = rawRotation > 0 && isExtremelyTall ? 0 : rawRotation;
    let angleDeg = -(matrixAngle + effectiveRotation);
    angleDeg = Math.round(angleDeg / 90) * 90;
    angleDeg = ((angleDeg % 360) + 360) % 360;
    return { angle: angleDeg, mirrored: [false, mirroredX] };
}
function buildModuleDrawProperties(resolved, record, sheetTextStyle, rootContext, pageNum) {
    const props = {};
    const { center, metadata } = resolved;
    const { angle: transformAngle, mirrored } = resolved.transform;
    let hasVisualLabels = false;
    for (const label of (0, AllegroRecordHelpers_1.iterateVisualLabels)(record)) {
        const propName = label.propertyName;
        if (propName === null || label.position === null) {
            continue;
        }
        hasVisualLabels = true;
        const upperName = propName.toUpperCase();
        const attrValue = metadata[upperName] ?? metadata[propName];
        if (attrValue === undefined || attrValue.length === 0) {
            continue;
        }
        const rendererKey = upperName === 'LOCATION' ? 'Part Reference'
            : upperName === 'VALUE' ? 'Value'
                : propName;
        const rad = (transformAngle * Math.PI) / 180;
        const cosA = Math.cos(rad);
        const sinA = Math.sin(rad);
        const dx = (mirrored[0] ? -1 : 1) * label.position.x * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM;
        const dy = label.position.y * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM;
        const pos = [center[0] + cosA * dx + sinA * dy, center[1] - sinA * dx + cosA * dy];
        const isRefdes = upperName === 'LOCATION';
        const isValue = upperName === 'VALUE';
        const layer = isRefdes ? NAME_LABEL_LAYER : VALUE_LABEL_LAYER;
        const rawJustify = cadenceJustToXY((0, decode_prop_block_1.findPropValue)(label.props, 'just'));
        const rjx = Math.round(cosA * rawJustify[0] + sinA * rawJustify[1]);
        const rjy = Math.round(-sinA * rawJustify[0] + cosA * rawJustify[1]);
        const justify = mirrored[0] ? [-rjx, rjy] : [rjx, rjy];
        const mirror = parseLabelMirror((0, decode_prop_block_1.findPropValue)(label.props, 'transform'));
        const primaryStyle = (0, AllegroResolveDesign_1.resolveExplicitTextStyle)(rootContext, pageNum, label.styleRowIndex, null, sheetTextStyle);
        const labelStyle = (0, AllegroResolveDesign_1.resolveExplicitTextStyle)(rootContext, pageNum, label.secondaryStyleRowIndex, null, primaryStyle);
        const labelColor = {};
        const labelAttr = [];
        if (labelStyle.bold) {
            labelAttr.push('bold');
        }
        if (labelStyle.italic) {
            labelAttr.push('italic');
        }
        const labelAttrField = labelAttr.length > 0 ? { attr: labelAttr } : {};
        props[rendererKey] = {
            value: attrValue,
            pos,
            layer,
            angle: 0,
            justify,
            size: labelStyle.size,
            thickness: 1,
            hidden: false,
            ...labelColor,
            ...labelAttrField,
            ...(isRefdes ? { ref_designator: true } : {}),
            ...(isValue ? { val: true } : {}),
            mirror,
        };
    }
    if (!hasVisualLabels) {
        const sheetColor = sheetTextStyle.color !== null ? { colorOverride: sheetTextStyle.color } : {};
        const sheetAttr = [];
        if (sheetTextStyle.bold) {
            sheetAttr.push('bold');
        }
        if (sheetTextStyle.italic) {
            sheetAttr.push('italic');
        }
        const sheetAttrField = sheetAttr.length > 0 ? { attr: sheetAttr } : {};
        const halfH = parseCdsLmanHalfHeight(metadata['CDS_LMAN_SYM_OUTLINE']);
        const offset = halfH + sheetTextStyle.size;
        props['Part Reference'] = {
            value: resolved.refdes,
            pos: [center[0], center[1] + offset],
            layer: NAME_LABEL_LAYER,
            angle: 0,
            justify: [-1, -1],
            size: sheetTextStyle.size,
            thickness: 1,
            hidden: false,
            ...sheetColor,
            ...sheetAttrField,
            ref_designator: true,
            mirror: [false, false],
        };
        if (resolved.value !== undefined && resolved.value.length > 0) {
            props['Value'] = {
                value: resolved.value,
                pos: [center[0], center[1] - offset],
                layer: VALUE_LABEL_LAYER,
                angle: 0,
                justify: [-1, -1],
                size: sheetTextStyle.size,
                thickness: 1,
                hidden: false,
                ...sheetColor,
                ...sheetAttrField,
                val: true,
                mirror: [false, false],
            };
        }
    }
    return props;
}
function cadenceJustToXY(raw) {
    const n = raw !== null ? parseInt(raw, 10) : NaN;
    if (!Number.isFinite(n) || n < 0 || n > 8) {
        return [-1, -1];
    }
    const col = n % 3;
    const row = Math.floor(n / 3);
    const x = col === 0 ? -1
        : col === 1 ? 0
            : 1;
    const y = row === 0 ? -1
        : row === 1 ? 0
            : 1;
    return [x, y];
}
function parseLabelMirror(transform) {
    const matrix = (0, AllegroRecordHelpers_1.parseTransformMatrix)(transform);
    if (matrix === null || matrix.length < 5) {
        return [false, false];
    }
    const det = matrix[0] * matrix[4] - matrix[1] * matrix[3];
    return [det < 0, false];
}
const SYM_OUTLINE_FALLBACK_HALF_HEIGHT_MM = 6.0;
function parseCdsLmanHalfHeight(raw) {
    const FALLBACK = SYM_OUTLINE_FALLBACK_HALF_HEIGHT_MM;
    if (raw === undefined || raw.length === 0) {
        return FALLBACK;
    }
    const parts = raw.split(',').map(Number);
    if (parts.length < 4 || parts.some((n) => !Number.isFinite(n))) {
        return FALLBACK;
    }
    const [, y1, , y2] = parts;
    const h = Math.abs(y2 - y1) * AllegroRecordHelpers_1.CADENCE_SCH_UNIT_TO_MM;
    return h > 0 ? h / 2 : FALLBACK;
}
//# sourceMappingURL=AllegroTransformModuleGenerator.js.map